/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { format, parseISO } from 'date-fns';
import { type ReactNode, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { type ConsultantInvoice } from '../../lib/api/consultant-invoices.js';
import { formatInr } from '../../lib/utils/money.js';
import { Badge, type BadgeTone } from '../ui/badge.js';

const STATUS_TONE: Readonly<Record<ConsultantInvoice['status'], BadgeTone>> = {
  draft: 'slate',
  submitted: 'amber',
  approved: 'green',
  rejected: 'rose',
  paid: 'violet',
};

interface CardProps {
  readonly invoice: ConsultantInvoice;
  readonly showTherapist?: boolean;
  readonly children?: ReactNode;
}

/** A consultant invoice summary card (CR-006) with a slot for role-specific actions. */
export function ConsultantInvoiceCard({
  invoice,
  showTherapist,
  children,
}: CardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-slate-800 dark:text-white">{invoice.invoiceNumber}</p>
            <Badge
              label={t(`consultantInvoice.status.${invoice.status}`)}
              tone={STATUS_TONE[invoice.status]}
            />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {format(parseISO(invoice.periodStart), 'MMM yyyy')}
            {showTherapist === true ? ` · ${invoice.therapistName}` : ''} ·{' '}
            {t('consultantInvoice.sessionsCount', { count: invoice.sessionsCount })}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-slate-800 dark:text-white">
            {formatInr(invoice.totalPaise)}
          </p>
          <p className="text-xs text-slate-400">
            {t('consultantInvoice.gstNote', {
              subtotal: formatInr(invoice.subtotalPaise),
              gst: formatInr(invoice.gstPaise),
            })}
          </p>
        </div>
      </div>
      {invoice.adminNote !== null && invoice.status === 'rejected' && (
        <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
          {t('consultantInvoice.rejectedNote', { note: invoice.adminNote })}
        </p>
      )}
      {children !== undefined && (
        <div className="mt-3 flex flex-wrap justify-end gap-2">{children}</div>
      )}
    </div>
  );
}
